import dayjs from "dayjs";
import {
  BoundingBox,
  LatLng,
  LatLon,
  Location,
  getBoundingBox,
  insideBoundingBox,
} from "geolocation-utils";
import {
  DocumentData,
  QuerySnapshot,
  DocumentSnapshot,
} from "firebase/firestore";

import { BOUNDING_BOXES } from "constants/locations";
import { COUNTRY_CODES_MAP } from "constants/country-codes";
import { IPlace, Boundary, Iterable, Bouinding, FirebaseBoundary } from "types";

// type Iterable = { id: string };
type Document = DocumentSnapshot<DocumentData>;
type Query = QuerySnapshot<DocumentData>;

export const castDoc = <T extends Iterable<unknown>>(document: Document) => {
  return { id: document.id, ...document.data() } as T;
};

export const castDocs = <T extends Iterable<unknown>>(list: Query) => {
  return list.docs.map((item) => castDoc<T>(item));
};

export const chunkArray = <T>(list: T[], size: number) => {
  const chunks: T[][] = [];

  for (let i = 0; i < list.length; i += size) {
    const slice = list.slice(i, i + size);
    chunks.push(slice);
  }

  return chunks;
};

export const bouindingFor = (countryCode: string) => {
  const item = BOUNDING_BOXES.find((item) => item.code === countryCode);

  return {
    box: item!.box,
    bounds: {
      west: item!.box[0],
      south: item!.box[1],
      east: item!.box[2],
      north: item!.box[3],
    },
  } as Bouinding;
};

export const coordinatesFromBBox = (bbox: number[]) => {
  const [west, south, east, north] = bbox;

  const topLeft = { lat: north, lon: west };
  const topRight = { lat: north, lon: east };
  const bottomLeft = { lat: south, lon: west };
  const bottomRight = { lat: south, lon: east };

  return { topLeft, topRight, bottomLeft, bottomRight };
};

export const createLatLng = (value?: string) => {
  if (value && value.length > 0) {
    if (value.includes(",")) {
      const array = value.split(",");
      if (array.length === 2) {
        const lat = array[0].trim();
        const lng = array[1].trim();

        const isNumeric = (str: string) => {
          return !isNaN(Number(str));
        };

        if (isNumeric(lat) && isNumeric(lng)) {
          return { lat: Number(lat), lng: Number(lng) };
        }
      }
    }
  }

  return null;
};

export const castBoundary = (document: Iterable<FirebaseBoundary>) => {
  const { id, geometry, ...props } = document;

  const { bbox, coordinates, type } = geometry;

  const parsedCoordinates = coordinates.map((i) => JSON.parse(i) as any[]);

  return {
    id,
    geometry: { bbox, type, coordinates: parsedCoordinates },
    ...props,
  } as Iterable<Boundary>;
};

type CSVHeaderData = { label: string; key: string };

export const prepareCollectedPlacesCSV = (data: IPlace[]) => {
  const filename = `Routes-Places-${dayjs().format("hh-mm-ss")}.csv`;

  const headers: CSVHeaderData[] = [
    { key: "countryCode", label: "Country Code" },
    { key: "name", label: "Name" },
    { key: "place_id", label: "Place ID" },
    { key: "formatted_address", label: "Address" },
    { key: "rating", label: "Rating" },
    { key: "user_ratings_total", label: "User Ratings Total" },
    { key: "type", label: "Google Type" },
    { key: "types", label: "Google Subtypes" },
    { key: "tags", label: "Our Tags" },
    { key: "url", label: "URL" },
  ];

  const csvData = data.map((item) => ({
    name: item.title,
    rating: item.rating.value,
    url: `https://maps.google.com/?cid=${item.cid}`,
    place_id: item.place_id,
    type: item.category ?? "-/-",
    tags: item.tags.join(", "),
    types: item.category_ids.join(", "),
    countryCode: item.address_info.country_code,
    formatted_address: item.address,
    user_ratings_total: item.rating.votes_count,
  }));

  return { headers, csvData, filename };
};

export const convertArrayToBoundingBox = (bbox: number[]) => {
  const topLeft = { latitude: bbox[3], longitude: bbox[0] };
  const bottomRight = { latitude: bbox[1], longitude: bbox[2] };

  return { topLeft, bottomRight } as BoundingBox;
};

export const convertArrayToBounds = (bbox: number[]) => {
  const [west, south, east, north] = bbox;
  return { west, south, east, north };
};

export const createBounds = (locations: { lat: number; lon: number }[]) => {
  const data = getBoundingBox(locations, 0);
  const topLeft = data.topLeft as unknown as LatLon;
  const bottomRight = data.bottomRight as unknown as LatLon;

  const bbox = [topLeft.lon, bottomRight.lat, bottomRight.lon, topLeft.lat];
  return convertArrayToBounds(bbox);
};

export const convertBBoxToBounds = (bbox: {
  topLeft: LatLng;
  bottomRight: LatLng;
}) => {
  const { topLeft, bottomRight } = bbox;
  const data = [topLeft.lng, bottomRight.lat, bottomRight.lng, topLeft.lat];
  return convertArrayToBounds(data);
};

export const calculateElavation = (elevations: number[]) => {
  let gain = 0;
  let loss = 0;

  for (let i = 1; i < elevations.length; i++) {
    const diff = elevations[i] - elevations[i - 1];

    if (diff > 0) gain += diff;
    else loss -= diff;
  }

  return {
    gain,
    loss,
    general: isNaN((gain / loss) * 100) ? 0 : (gain / loss) * 100,
  };
};

interface Point {
  lat: number;
  lng: number;
}

const deg2rad = (deg: number): number => {
  return deg * (Math.PI / 180);
};

const dotProductAngle = (p0: Point, p1: Point, p2: Point): number => {
  const v1 = { x: p1.lat - p0.lat, y: p1.lng - p0.lng };
  const v2 = { x: p2.lat - p1.lat, y: p2.lng - p1.lng };
  const dotProduct = v1.x * v2.x + v1.y * v2.y;
  const magV1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
  const magV2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
  const angle = Math.acos(dotProduct / (magV1 * magV2));
  return angle;
};

const haversine = (p1: Point, p2: Point): number => {
  const R = 6371; // Earth's radius in km
  const dLat = deg2rad(p2.lat - p1.lat);
  const dLng = deg2rad(p2.lng - p1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(p1.lat)) *
      Math.cos(deg2rad(p2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
};

export const calculateCurvature = (points: Point[]): number => {
  const distances = [];
  const angles = [];

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    // Calculate distance between adjacent points using the Haversine formula
    const distance = haversine(p1, p2);
    distances.push(distance);

    if (i > 0) {
      const p0 = points[i - 1];

      // Calculate angle between adjacent line segments using the dot product formula
      const angle = dotProductAngle(p0, p1, p2);
      angles.push(angle);
    }
  }

  const curvatures = [];

  for (let i = 0; i < angles.length; i++) {
    const curvature = angles[i] / distances[i];
    curvatures.push(curvature);
  }

  // console.log({ points: points.length });
  // console.log({ curvatures: curvatures });

  const maxCurvature = curvatures.reduce((a, b) => (a > b ? a : b));

  const curvaturesPercentages = curvatures.map((curvature) =>
    isNaN(curvature) ? 0 : (curvature / maxCurvature) * 100
  );

  const mediumCurvaturePercentage =
    (curvaturesPercentages.reduce((a, b) => a + b) /
      curvaturesPercentages.length) *
    100;

  // console.log({ mediumCurvaturePercentage });

  return mediumCurvaturePercentage;
};

export const circleToBoundingBox = (
  centerLat: number,
  centerLng: number,
  radius: number
) => {
  const earthRadius = 6371; // Earth's radius in kilometers
  const latDiff = (radius / earthRadius) * (180 / Math.PI); // Latitude difference in degrees
  const lngDiff =
    ((radius / earthRadius) * (180 / Math.PI)) /
    Math.cos((centerLat * Math.PI) / 180); // Longitude difference in degrees

  const topLeftLat = centerLat + latDiff;
  const topLeftLng = centerLng - lngDiff;
  const bottomRightLat = centerLat - latDiff;
  const bottomRightLng = centerLng + lngDiff;

  return {
    topLeft: { lat: topLeftLat, lng: topLeftLng },
    bottomRight: { lat: bottomRightLat, lng: bottomRightLng },
  };
};

export const getCountryISO2 = (iso3Code: string) => {
  return Object.keys(COUNTRY_CODES_MAP).find(
    (item) => COUNTRY_CODES_MAP[item] === iso3Code
  );
};

export const getCountryISO3 = (iso2Code: string): string | undefined => {
  const value = COUNTRY_CODES_MAP[iso2Code];
  return value ?? undefined;
};

export const getKeyByValue = <T = unknown, V = {}>(value: T, object: V) => {
  const indexOf = Object.values(object as {}).indexOf(
    value as unknown as typeof object
  );
  return indexOf === -1 ? undefined : Object.keys(object as {})[indexOf];
};

export const isKeyExists = <V = {}>(value: string, object: V) => {
  return Object.keys(object as {}).includes(value) ?? false;
};

export const convertMillisecondsToHours = (value: number) => {
  const millisecondsInHour = 1000 * 60 * 60;
  const hours = value / millisecondsInHour;
  return hours;
};

export const placesInRouteBbox = (
  locations: Location[],
  places: Iterable<IPlace>[],
  margin = 0
) => {
  const bouindingBox = getBoundingBox(locations, margin);

  return places.filter((item) => {
    const [lat, lng] = [item.latitude, item.longitude];
    const location = { lat, lon: lng };
    return insideBoundingBox(location, bouindingBox);
  });
};

export const getHours = (value: number) => {
  const hours = value / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);

  return { h: String(rhours), m: `${rminutes === 0 ? "00" : rminutes}` };
};

export const capitalize = (value?: string) => {
  if (!value) return "";
  return value[0].toUpperCase() + value.slice(1);
};

export const getCountryName = (iso2Code: string) => {
  const data = BOUNDING_BOXES.find((item) => item.code === iso2Code);
  return data?.name ?? "";
};

// Convert javascript dot notation object to nested object
// Code was transcripted from StackOverflow: https://stackoverflow.com/questions/7793811/convert-javascript-dot-notation-object-to-nested-object
export const transpose = (object: { [key: string]: any }) => {
  const parseDotNotation = (
    string: string,
    value: any,
    data: { [key: string]: any }
  ) => {
    let currentObj = data,
      keys = string.split("."),
      i,
      l = Math.max(1, keys.length - 1),
      key;

    for (i = 0; i < l; ++i) {
      key = keys[i];
      currentObj[key] = currentObj[key] || {};
      currentObj = currentObj[key];
    }

    currentObj[keys[i]] = value;
    delete data[string];
  };

  for (const key in object) {
    if (key.indexOf(".") !== -1) {
      parseDotNotation(key, object[key], object);
    }
  }
  return object;
};

export const formatSeconds = (value: number) => {
  if (value > 60) {
    return `${Math.floor(value / 60)} min ${(
      "0" + Math.floor(value % 60)
    ).slice(-2)} sec`;
  }

  return `${value} sec`;
};
